<script>
import axios from 'axios';
import { duplicateCompany } from '../../../../../backend/database/admin';

export default {
    props: {
        companyId: {
            type: Number,
            required: true
        },
    },

    data() {
        return {
            loading: true,
            originalCompanyDetails: null,
            companyDetails: null,

            unchangedUsersMap: {},
            duplicationTried: false,
            duplicationInProgress: false,
        }
    },

    computed: {
        hasDuplicates() {
            this.checkUnchangedUserEmails();
            return Object.values(this.unchangedUsersMap).some(val => val) || 
                this.companyDetails.email === this.originalCompanyDetails.email ||
                this.companyDetails.name === this.originalCompanyDetails.name ||
                this.companyDetails.phone === this.originalCompanyDetails.phone;
        },
    },

    async mounted() {
        if (this.companyId) {
            await this.fetchCompanyDetails();
        }

        this.loading = false;
    },

    methods: {
        async fetchCompanyDetails() {
            try {
                const response = await axios.get(`/api/admin/get-company-details/${this.companyId}`, {withCredentials: true});
                this.companyDetails = structuredClone(response.data);
                this.originalCompanyDetails = response.data;

                for (let idx = 0; idx < this.companyDetails.users.length; idx++) {
                    const user = this.companyDetails.users[idx];
                    const mail_idx = user.email.indexOf('@');
                    if (mail_idx !== -1) {
                        user.email = user.email.substring(0, mail_idx) + '_dup@' + user.email.substring(mail_idx + 1);
                    } else {
                        user.email = user.email + '_dup';
                    }
                }
            } catch (error) {
                console.error("Error fetching company details:", error);
            }
        },

        async duplicateCompany() {
                try {
                    this.duplicationTried = true;
                    if (this.hasDuplicates) {
                        this.$notify('error', "Please perform changes on vital mail information.", true);
                        return;
                    }

                    this.duplicationInProgress = true;

                    const payload = {
                        company_data: {
                            company_id: this.companyId,
                            name: this.companyDetails.name,
                            fullname: this.companyDetails.fullname,
                            email: this.companyDetails.email,
                        },
                        users: this.companyDetails.users,
                    };
                    
                    const response = await axios.post(`/api/admin/duplicate-company`, payload, {withCredentials: true});
                    if (response.status === 200) {
                        this.$notify('success', response.data, true);
                        this.$emit('result', true);
                    } else {
                        this.$notify('error', response.data, true);
                    }

                    this.duplicationInProgress = false;
                } catch (error) {
                    this.duplicationInProgress = false;
                    this.$notify('error', "Error duplicating company: " + error, true);
                }
        },

        checkUnchangedUserEmails() {
            const users = this.companyDetails.users;

            for (let idx = 0; idx < users.length; idx++) {
                const user = users[idx];
                const original_user = this.originalCompanyDetails.users[idx];

                if (user.email === original_user.email)
                    this.unchangedUsersMap[user.id] = true;
                else
                    this.unchangedUsersMap[user.id] = false;
            }
        },
    }
}
</script>

<template>
    <div class="flex-all-centered-col">
        <div v-if="loading" class="flex-all-centered-row">
            <loading-spinner fontSize="32px" />
        </div>

        <div v-else-if="!loading && !companyDetails" class="flex-allc-centered-row">
            <p class="error-message">
                Error loading company details. Please try again later.
            </p>
        </div>

        <div v-else class="flex-all-centered-col">
            <div class="flex-all-centered-row section-title">
                Company Details
            </div>
            <div class="flex-all-centered-row">
                <div class="flex-all-centered-col" style="align-items: flex-start;">
                    <label for="company_name">Company Name</label>
                    <input :class="{'unchanged-input': duplicationTried && originalCompanyDetails.name === companyDetails.name}" 
                        id="company_name" type="text" v-model="companyDetails.name"
                    >
                </div>
                <div class="flex-all-centered-col" style="align-items: flex-start;">
                    <label for="company_fullname">Company Fullname</label>
                    <input id="company_fullname" type="text" v-model="companyDetails.fullname">
                </div>
                <div class="flex-all-centered-col" style="align-items: flex-start;">
                    <label for="company_email">Company Email</label>
                    <input :class="{'unchanged-input': duplicationTried && originalCompanyDetails.email === companyDetails.email}" 
                        id="company_email" type="text" v-model="companyDetails.email"
                    >
                </div>
                <div class="flex-all-centered-col" style="align-items: flex-start;">
                    <label for="company_phone">Company Phone</label>
                    <input :class="{'unchanged-input': duplicationTried && originalCompanyDetails.phone === companyDetails.phone}" 
                        id="company_phone" type="text" v-model="companyDetails.phone"
                    >
                </div>
            </div>

            <div class="flex-all-centered-col section">
                <div class="flex-all-centered-row section-title">
                    Company Users
                </div>
                <table>
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Surname</th>
                            <th>Email</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="user in companyDetails.users" :key="user.id">
                            <td><input type="text" v-model="user.name"></td>
                            <td><input type="text" v-model="user.surname"></td>
                            <td><input type="text" :class="{'unchanged-input': duplicationTried && unchangedUsersMap[user.id] === true}" v-model="user.email"></td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div class="flex-all-centered-row" style="justify-content: flex-end; margin-top: 1rem;">
                <button class="sust-btn sust-btn-red" @click="$emit('result', false)"
                    style="margin-right: 1rem;" :disabled="duplicationInProgress"
                >
                    Cancel
                </button>
                <button class="sust-btn sust-btn-green" @click="duplicateCompany()"
                    :disabled="duplicationInProgress"
                >
                    <div v-if="!duplicationInProgress">
                        Duplicate Company
                    </div>
                    <div v-else>
                        <loading-spinner color="whitesmoke" />
                    </div>
                </button>
            </div>
        </div>
    </div>
</template>

<style lang='scss' scoped>
.section {
    width: 100%;
    justify-content: flex-start;
}

.section-title {
    font-size: 1.2rem;
    font-weight: bold;
    margin: 0.5rem 0;
    justify-content: flex-start;
}

table {
    width: 100%;
    border-collapse: collapse;
}

tbody tr {
    height: 2.5rem;
}

label {
    line-height: 20px;
}

input {
    border: 0 !important;
    width: 90%;
    padding: .2rem;
    outline: thin solid lightgray;
    border-radius: .2rem;

    &:focus {
        outline: thin solid black !important;
    }
}

.unchanged-input {
    box-shadow: 0 0 5px 2px var(--invalid-input);
}
</style>